import React, { useRef } from 'react';
import { Descriptions, Image, Tag, Typography } from 'antd';
import { routeHistory } from '@nackle/biw-web-router';
import { Routes } from '../../../routes';
import { renderWithSearchWords } from '../../components/SearchBar/SearchBar';
import useDeleteResource from '../../gql/hooks/useDeleteResource'; 
import SearchQueryList from '../../components/SearchQueryList/SearchQueryList';
import { useFeatureFlags } from '../../../featureFlags';
import { flags } from '../../../featureFlags/flags';
import { MISSING_IMAGE } from '../../consts';
import { ColoredTag, ViewPageActions } from '../GenericCreateEdit/GenericElements';
import { DEFAULT_LOCALE_CODE } from '../../../const';
let expandedRows = [];

const ResourcesListPage = () => { 
    const listPageRef = useRef( null );

    const { deleteResource, isDeleting } = useDeleteResource();
    const [ showBrands ] = useFeatureFlags( [ flags.BRANDS ] );

    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Learning Content', path: Routes.LearningContentNav.buildPath() },
        { name: 'Resources' }
    ];

    const uiStrings = {
        cardTitle: 'Resources',
        createButtonTitle: 'Create New Resource'
    };

    async function handleDeleteClick ( item ) {
        await deleteResource( item.id, () => {
            listPageRef.current.reload();
        } );
    }

    function handleEditClick( item ) {
        routeHistory.push( Routes.EditResource.buildPath( item.id ) );
    }

    const handleCreateClick = () => {
        routeHistory.push( Routes.CreateResource.buildPath() );
    };

    const handleSetExpandedRows = ( rows ) => {
        expandedRows = rows;
    };


    const renderExpandedRow = ( item ) => {
        return (
            <div className="row-expand">
                <div className="flex-container">
                    <div className="flex-left">
                        <Descriptions bordered layout='vertical' items={ [
                            {
                                key: 'desc',
                                label: `Description (${ DEFAULT_LOCALE_CODE })`,
                                children: item.content?.length > 0 ?
                                    <Typography.Paragraph style={ { marginTop: 8 } }>
                                        { item.content }
                                    </Typography.Paragraph> :
                                    <span className='canceled'>No description</span>
                            }, {
                                key: 'locs',
                                label: 'Translated Locales',
                                children: item.localizations?.length > 0 ? item.localizations
                                    .toSorted( ( a, b ) => a.localeCode === DEFAULT_LOCALE_CODE ? -1 : b.localeCode === DEFAULT_LOCALE_CODE ? 1 : 0 )
                                    .map( i => <Tag key={ i.localeCode } color={ i.localeCode === DEFAULT_LOCALE_CODE ? 'green' : 'blue' }>{ i.localeCode }</Tag> )
                                    : <span className='canceled'>No translations</span>
                            }
                        ] } />
                    </div>
                </div>
            </div>
        );
    };

    const tableColumns = [
        {
            dataIndex: 'image',
            key: 'thumbnail',
            title: 'Thumbnail',
            width: 120,
            fixed: 'left',
            render: ( image ) =>
                <Image
                    width="100px"
                    className="thumbnail-image"
                    fallback={ MISSING_IMAGE }
                    src={ image || MISSING_IMAGE }
                    preview={ !!image }
                />
        },
        {
            dataIndex: 'name',
            key: 'name', 
            title: 'Name',
            sorter: ( a, b ) => 0,
            sortKey: 'name',
            width: 350,
            render: ( name, i ) => {
                if ( !i.content && !i.name ) {
                    return <span className="no-translation">No Name</span>;
                } else {
                    return (
                        <span className="name-column">
                            { renderWithSearchWords( name, listPageRef.current.getSearchWords() ) }
                        </span> 
                    );
                }
            }
        },
        {
            dataIndex: 'isFeatured',
            key: 'isFeatured',
            title: 'Featured',
            render: ( isFeatured ) => <ColoredTag.Featured isFeatured={ isFeatured } />
        },
        {
            dataIndex: 'type',
            key: 'type',
            title: 'Type',
            render: ( type ) => <ColoredTag.Icon value={ type } />
        },
        ...( showBrands ?
            [
                {
                    dataIndex: 'brandIds',
                    key: 'brands',
                    title: 'Brand',
                    sortKey: 'brands',
                    render: ( b ) => {
                        return b.map( i => {
                            const brand = listPageRef.current.getBrand( i );
                            return brand ? <ColoredTag key={ brand.id } value={ brand.name } /> : null ;
                        } );
                    }
                },
            ] : [] ),
        {
            dataIndex: 'isPublished',
            key: 'isPublished',
            title: 'Published',
            render: ( isPublished ) => <ColoredTag.Publish isPublished={ isPublished } />
        },
        {
            dataIndex: 'createdAt',
            key: 'createdAt',
            title: 'Created At',
            sorter: ( a, b ) => 0,
            sortKey: 'createdAt',
            render: ( createdAt ) => <span>{ createdAt }</span>
        },
        {
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            title: 'Updated At',
            sorter: ( a, b ) => 0,
            sortKey: 'updatedAt',
            render: ( updatedAt ) => <span>{ updatedAt }</span>
        },
        {
            title: 'Actions',
            key: 'actions', 
            fixed: 'right', 
            align: 'center',
            width: '100px',
            render: ( item ) => <ViewPageActions item={ item } handleDeleteCb={ handleDeleteClick } handleEditCb={ handleEditClick } />
        }
    ];   
  
    return (
        <SearchQueryList 
            ref={ listPageRef }
            type="resources" 
            uiStrings={ uiStrings }
            breadcrumbs={ breadcrumbs }
            tableColumns={ tableColumns }
            isDeleting={ isDeleting }
            renderExpandedRowCallback= { renderExpandedRow }
            setExpandedRowsCallback= { handleSetExpandedRows }
            defaultExpandedRows= { expandedRows }
            createClickCallback={ handleCreateClick }
        />
    );
};


export default ResourcesListPage;
